// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- `virtual:i18n` has no file to import types from, and its module declaration reaches each app's program only through a reference
/// <reference path="./virtual.d.ts" />
import { appDir, catalogs, i18nMessages } from "virtual:i18n";
import { createElement, Fragment, useSyncExternalStore, type ReactNode } from "react";
import { i18n } from "./macro-runtime";
import { namespaceForFile, startupNamespacesFor } from "./namespaces";

export { i18n };

// `virtual:i18n` is generated at build time by `@web-monorepo/i18n/vite`: it slices the single,
// repo-wide `/locales/{locale}.po` into one lazy chunk per namespace, keyed
// `"{locale}/{namespace}"`. Nothing
// here reads the file system, and no namespace exists as a file. A session downloads `common` plus
// the namespaces of the routes it visits.
//
// English is not a catalog. The macros keep each message's English at its call site (see
// ../vite/lingui-macro.ts), and Lingui renders it whenever the active locale has no translation for
// the id: in an English session, for a string Crowdin has not translated yet, and when a locale's
// catalog failed to load. So English is never fetched and cannot fail to load. That English is
// compiled at build time like the catalogs, so there is no message compiler here.
const SOURCE_LOCALE = "en";

/** `es-ES/common` -> `es-ES` */
const available = new Set(Object.keys(catalogs).map((key) => key.slice(0, key.indexOf("/"))));

/**
 * The catalog to use for a browser language, or null when English should be used: the language is
 * English, or there is no catalog for it.
 *
 * Crowdin names catalogs by region (`%locale%` expands to `es-ES`, `fr-FR`), while browsers often
 * send a bare language (`es`) or a region we have no catalog for (`es-419`). So: the exact tag,
 * then the bare language, then any catalog in the same language. The last step is what lets a
 * browser saying `es` reach `es-ES.po`; the sort makes the choice deterministic when a language has
 * more than one region. It stops short for English, so `en-US` gets the code's own English rather
 * than British spellings from `en-GB.po`.
 */
export function resolveLocale(language: string): string | null {
  const tag = language.replace("_", "-");
  const base = tag.split("-")[0] ?? tag;
  if (available.has(tag)) return tag;
  if (base === SOURCE_LOCALE) return null;
  if (available.has(base)) return base;
  return [...available].sort().find((locale) => locale.split("-")[0] === base) ?? null;
}

/**
 * The browser's language, or `"en"` where there is no `navigator`. Node before 21 has none, so
 * reading it unguarded would make `activateLocale()` throw there rather than fall back to the
 * source locale. Later Node has one, reporting the host's locale.
 */
function browserLanguage(): string {
  return typeof navigator === "undefined" ? "en" : navigator.language;
}

/** Null until `activateLocale` picks one. */
let activeLocale: string | null = null;

/** The active locale once its startup catalogs have settled: what `LocaleGate` waits for. */
let readyLocale: string | null = null;

/** The locale used for remounts; English when no startup catalog was loaded. */
let shownLocale: string = SOURCE_LOCALE;

/** Every mounted `LocaleGate`, told when `readyLocale` changes. */
const listeners = new Set<() => void>();

// A data router starts loading the initial route's `lazy` as soon as it is created, which can be
// before `LocaleGate` has rendered and called `activateLocale`. Those loads wait on this rather than
// skipping the namespace, or the first page would render without its strings.
let markActivated: () => void = () => undefined;
const activated = new Promise<void>((resolve) => {
  markActivated = resolve;
});

/**
 * Keyed `"{locale}/{namespace}"`, so concurrent routes needing the same catalog await one fetch.
 *
 * The locale is in the key rather than implied by `activeLocale` so the cache cannot outlive the
 * locale it was filled for: a second `activateLocale` (a test, or a future language switcher)
 * starts from an empty set of hits instead of silently serving the previous locale's promises.
 */
const inFlight = new Map<string, Promise<void>>();

/**
 * A namespace with nothing translated yet has no catalog, and English has none at all: either way
 * there is nothing to load and its strings render in English. A catalog that fails to load rejects
 * after the build's own retries; its strings stay English, and `loadNamespace` evicts the failure
 * so the next route load tries again.
 */
async function fetchNamespace(locale: string, namespace: string): Promise<void> {
  const catalog = await catalogs[`${locale}/${namespace}`]?.();
  if (catalog) i18n.load(locale, catalog.messages);
}

/**
 * Load one namespace by name for the active locale, waiting for `activateLocale` if it has not run
 * yet. Most callers want `loadNamespaceForFile`.
 */
export async function loadNamespace(namespace: string): Promise<void> {
  if (!activeLocale) await activated;
  const locale = activeLocale;
  if (!locale) return;

  const key = `${locale}/${namespace}`;
  let pending = inFlight.get(key);
  if (!pending) {
    // Evict on rejection, or a single transient failure (a CDN blip, a 404 against a chunk from a
    // half-rolled deploy) would be cached for the life of the session and every later navigation
    // into this namespace would re-throw it. Dropping the entry makes the next one retry.
    pending = fetchNamespace(locale, namespace).catch((error: unknown) => {
      inFlight.delete(key);
      throw error;
    });
    inFlight.set(key, pending);
  }
  return pending;
}

/**
 * Load the catalog covering `moduleKey`, an app-relative module path, and resolve once its strings
 * are live.
 *
 * Call it from the router's `lazy`, which React Router awaits before it renders the route — that
 * await is what makes the no-hook design work. There is no subscription, so a catalog arriving
 * after a component mounts could never repaint it; blocking the route on its own catalog means one
 * never does. The route chunk and the catalog chunk are fetched together, so this costs no latency.
 *
 * The catalog is repo-wide, so its `#:` origins are repo-relative (`marketplace/src/pages/...`)
 * while a router module key is app-relative (`/src/pages/...`). `appDir` comes from the same build
 * plugin that did the split, so the split and this lookup give the same answer for the same file.
 */
export function loadNamespaceForFile(moduleKey: string): Promise<void> {
  return loadNamespace(namespaceForFile(`${appDir}/${moduleKey.replace(/^\/+/, "")}`));
}

/**
 * Makes `language`'s locale the active one and loads its startup catalogs. Apps don't call this
 * themselves: they render through `LocaleGate`, which does, and tests and stories call it through
 * `activateLocaleForTests`.
 *
 * There is no React context and no provider: `t`, and `<Trans>` through ./macro-runtime, read the
 * `i18n` singleton at render time, so nothing needs to subscribe to it. Nothing re-renders when the
 * locale changes, either; `LocaleGate` mounting its children anew in the new locale is what does.
 *
 * Loads `common` and the namespace matching the app root. An app-wide namespace (Toolkit's)
 * can therefore cover an eager router without shipping its messages to other apps. Per-route
 * namespaces still arrive through `loadNamespaceForFile`.
 *
 * After every load settles, activation rejects with the first failure in startup order. The
 * locale stays active: failed catalogs fall back to English, while successful ones remain available.
 * Failed loads are not cached (see `inFlight`), so later route loads and activations retry them.
 */
export async function activateLocale(language: string = browserLanguage()): Promise<void> {
  // A language we have no catalog for gets English, from each call site.
  const locale = resolveLocale(language) ?? SOURCE_LOCALE;

  // Seed with a fresh object holding the locale's built-in strings (the `i18n` namespace), and
  // activate before any catalog lands. `i18n.load` merges via `Object.assign` into whatever object it
  // holds for the locale, so a fresh one also keeps later namespaces from being assigned into the
  // first chunk's own exported `messages`.
  activeLocale = locale;
  // Whichever locale was ready no longer is: `LocaleGate` waits for this one, even when asked for
  // the previous one again before this one is in.
  readyLocale = null;
  i18n.load(locale, { ...i18nMessages[locale] });
  i18n.activate(locale);
  markActivated();

  let loaded = false;
  try {
    // Wait for every startup catalog even when one fails, so none arrive after the gate paints.
    // loadNamespace also no-ops for absent catalogs; exclude them here so a no-op cannot count
    // as a successful load and remount an English page.
    const results = await Promise.allSettled(
      startupNamespacesFor(appDir)
        .filter((namespace) => catalogs[`${locale}/${namespace}`])
        .map((namespace) => loadNamespace(namespace)),
    );
    // A successful subset still changes messages on screen. Remount the page in that locale
    // even when another startup catalog failed, so a page shown during the grace period updates.
    loaded = results.some((result) => result.status === "fulfilled");
    const failure = results.find((result) => result.status === "rejected");
    if (failure?.status === "rejected") throw failure.reason;
  } finally {
    // Unless another locale was activated in the meantime, which is then the one to wait for.
    if (activeLocale === locale) {
      readyLocale = locale;
      shownLocale = loaded ? locale : SOURCE_LOCALE;
      for (const listener of listeners) listener();
    }
  }
}

/**
 * How long `LocaleGate` holds the page back for a catalog before it shows the page in English. A
 * catalog that arrives in time means a page that paints translated, with no English first.
 */
export const LOCALE_GRACE_MS = 100;

interface Gating {
  /** Settles once `activateLocale` finishes; superseded by another locale, it leaves this one not ready. */
  activation: Promise<void>;
  /** Settles once `activation` does or the grace period is over, whichever comes first. */
  held: Promise<void>;
  /** The grace period is over and the catalog is still loading. */
  overdue: boolean;
}

/** Keyed by locale, while `LocaleGate` waits for one. Shared, so a locale is activated once. */
const gating = new Map<string, Gating>();

/**
 * Activates `locale` for every gate waiting on it. The grace timer runs whether or not a gate shows
 * `pending`: gates with and without it can wait on the same locale, and one timer is cheap.
 */
function startGating(locale: string, onError: (error: unknown) => void, overdue: boolean): Gating {
  // Made first, so the callbacks below can check they still belong to the current entry.
  const entry: Gating = { activation: Promise.resolve(), held: Promise.resolve(), overdue };
  const activation = activateLocale(locale).catch((error: unknown) => {
    // Reported only by the current entry: going back to a locale still loading starts a second
    // activation awaiting the same load, and its failure is one failure. And only while the page
    // is in that locale: a locale it has left is loaded again if it comes back.
    if (gating.get(locale) === entry && activeLocale === locale) onError(error);
  });
  const grace = new Promise<void>((resolve) => setTimeout(resolve, LOCALE_GRACE_MS));
  entry.activation = activation;
  entry.held = Promise.race([activation, grace]);
  void grace.then(() => {
    if (gating.get(locale) === entry) entry.overdue = true;
  });
  // Identity-checked, so a superseded activation settling late cannot remove its replacement.
  void activation.finally(() => {
    if (gating.get(locale) === entry) gating.delete(locale);
  });
  gating.set(locale, entry);
  return entry;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getReadyLocale = () => readyLocale;

interface LocaleGateProps {
  /** The session's language, or null when there is none (signed out): the browser's is used. */
  language: string | null | undefined;
  /**
   * Told when the locale's catalog fails to load, once per failure, unless the page has moved to
   * another locale by then. Its strings render in English. Pass a stable function, not a closure
   * over render state: the one passed when the gate last started waiting for the locale is called.
   */
  onError: (error: unknown) => void;
  /**
   * Shown beside the page while it is in English, waiting for its catalog: say translations are on
   * their way. Its strings belong in `I18N_FILES` (./namespaces.ts), the only ones translated by then.
   * Without it (or with `null`) there is no English page: the gate holds the page back until the
   * catalog is in.
   */
  pending?: ReactNode;
  children?: ReactNode;
}

/**
 * Renders its children in `language`'s locale, once that locale's startup catalogs settle.
 * Put it under a `<Suspense>`.
 *
 * A data router's `RouterProvider` goes above the gate, as home's does, or under it wrapped the way
 * marketplace's `SubscribedRouterProvider` is: rendered by the gate, it can miss the router's first
 * update and stay on its loading fallback.
 *
 * With `pending`, it suspends for up to `LOCALE_GRACE_MS`, and the Suspense fallback shows. A
 * catalog in by then paints translated straight away. One that takes longer gets the page in
 * English, with `pending` beside it, so nobody waits on a slow connection for a page they can
 * already use, and the page is rendered again in its language when the catalog lands. Without
 * `pending`, it suspends until the catalog is in, however long that takes.
 *
 * The children are keyed by the locale their strings are in, so they mount
 * anew when it changes. Strings read the `i18n` singleton as they render, with no hook or
 * subscription, so a component that does not render again keeps its old language, and memoized
 * ones (React Router's route tree among them) do not render again when a parent does. Remounting
 * is the one way to reach them all. It costs the page's state, which happens when the language
 * changes in place (signing in, a language picker) or a slow catalog lands. A change in place
 * whose catalog is slow does both, mounting the page in English and then in the new language.
 *
 * A catalog that fails to load is reported to `onError`, and its strings render in English.
 * Successfully loaded startup catalogs still repaint the page. If every catalog fails, a page
 * already showing in English stays mounted. Without `pending`, the page waits until the loads
 * and their retries have settled. Tests and
 * stories activate their locale before rendering (`activateLocaleForTests`), so the gate has
 * nothing to wait for there.
 */
export function LocaleGate({ language, onError, pending, children }: LocaleGateProps) {
  const ready = useSyncExternalStore(subscribe, getReadyLocale, getReadyLocale);
  const locale = resolveLocale(language ?? browserLanguage()) ?? SOURCE_LOCALE;
  const waiting = ready !== locale;

  if (waiting) {
    // An entry for a locale that is no longer the active one was superseded by another language
    // meanwhile. Nothing will make this locale ready while another is active, so start again,
    // carrying over its `overdue` flag so a locale already past its grace period isn't held back again.
    const current = gating.get(locale);
    const entry =
      current && activeLocale === locale ? current : startGating(locale, onError, current?.overdue ?? false);
    // How a component suspends in React 18: Suspense renders it again once this settles.
    if (pending == null) throw entry.activation;
    if (!entry.overdue) throw entry.held;
  }

  return createElement(
    Fragment,
    null,
    createElement(Fragment, { key: waiting ? SOURCE_LOCALE : shownLocale }, children),
    waiting ? pending : null,
  );
}

/**
 * Joins items the way the active locale writes a list: `Reading, Phonics, Dyslexia` in English,
 * `Reading Phonics Dyslexia` in Japanese, `Reading, Phonics und Dyslexia` in German. Use it instead
 * of `.join(", ")`, which hard-codes English punctuation into a translated screen. The separators
 * come from the browser's CLDR data, so there is nothing to translate.
 */
export function formatList(items: readonly string[]): string {
  return new Intl.ListFormat(i18n.locale, { style: "short", type: "unit" }).format(items);
}
